import React from 'react';
import { stripAcrosticMarkers } from './lyricsDetection';

// Gold/amber colour for acrostic highlighting. Deliberately the literal hex rather
// than the `accent-gold` token (#f59e0b): this matches jnana-native's
// ACROSTIC_HIGHLIGHT_COLOR so the same bhajan reads identically on both surfaces,
// and it keeps highlighted syllables distinct from the active-line colour, which
// LyricsRenderer already paints with `text-accent-gold`.
const ACROSTIC_HIGHLIGHT_COLOR = '#B8860B';

export interface AcrosticSegment {
  text: string;
  highlighted: boolean;
}

/**
 * Parse text for acrostic markers (%text%) and return segments.
 *
 * Ported from jnana-native — same regex, same segment boundaries. `[^%]+` cannot
 * nest and requires at least one character, so `%%` and a lone `%` never match and
 * are carried through as plain text.
 */
export function parseAcrosticMarkers(text: string): AcrosticSegment[] {
  const segments: AcrosticSegment[] = [];
  const regex = /%([^%]+)%/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      segments.push({ text: text.slice(lastIndex, match.index), highlighted: false });
    }
    // Add the highlighted text (without the % markers)
    segments.push({ text: match[1], highlighted: true });
    lastIndex = regex.lastIndex;
  }

  // Add remaining text after the last match
  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), highlighted: false });
  }

  return segments;
}

export interface AcrosticTextProps {
  /** The text to render, potentially containing %markers%. */
  text: string;
  /** Whether acrostic highlighting is enabled. */
  isAcrosticMode: boolean;
  className?: string;
}

/**
 * Renders lyric text with acrostic highlighting: content wrapped in %markers% is
 * shown in gold at weight 700, with the % characters removed.
 *
 * DIVERGENCE FROM jnana-native: native renders the raw text — % characters and all —
 * when acrostic mode is off, because there the mode is a per-user permission and the
 * markers are considered editorial content. jnana-web is anonymous and has no such
 * gate, so a literal % is only ever a rendering bug. With the mode off we therefore
 * strip the markers instead of showing them.
 */
export const AcrosticText: React.FC<AcrosticTextProps> = ({
  text,
  isAcrosticMode,
  className,
}) => {
  if (!isAcrosticMode) {
    return <span className={className}>{stripAcrosticMarkers(text)}</span>;
  }

  const segments = parseAcrosticMarkers(text);

  if (segments.length === 0) {
    return <span className={className}>{text}</span>;
  }

  return (
    <span className={className}>
      {segments.map((segment, idx) =>
        segment.highlighted ? (
          <span key={idx} className="font-bold" style={{ color: ACROSTIC_HIGHLIGHT_COLOR }}>
            {segment.text}
          </span>
        ) : (
          // Fragment, not a span: unmarked runs must not add wrapper noise to the DOM.
          <React.Fragment key={idx}>{segment.text}</React.Fragment>
        )
      )}
    </span>
  );
};

export default AcrosticText;
